import { useEffect, useRef, useState } from "react";
import { useValue } from "@tldraw/state-react";
import { FileHelpers } from "@tldraw/utils";
import { defaultShapeSchemas } from "@tldraw/tlschema";
import type {
  TLAssetStore,
  TLImageAsset,
  TLImageShape,
} from "@tldraw/tlschema";
import { useEditor } from "../../hooks";
import type { PlanroomEditor } from "../../editor";
import { BaseBoxShapeUtil, HTMLContainer } from "../shape-util";

// Images: an asset drawn at the resolution the current zoom needs, with crop and flips.

export class ImageShapeUtil extends BaseBoxShapeUtil<TLImageShape> {
  static override type = "image";
  static override props = defaultShapeSchemas.image.props;
  getDefaultProps(): TLImageShape["props"] {
    return {
      w: 100,
      h: 100,
      playing: true,
      url: "",
      assetId: null,
      crop: null,
      flipX: false,
      flipY: false,
      altText: "",
    };
  }
  override isAspectRatioLocked(_shape: TLImageShape) {
    return true;
  }
  override canCrop(_shape: TLImageShape) {
    return true;
  }
  override getIndicatorPath(shape: TLImageShape) {
    return `M0,0H${shape.props.w}V${shape.props.h}H0Z`;
  }
  component(shape: TLImageShape) {
    return <ImageBody shape={shape} />;
  }
  override cropPreview(shape: TLImageShape) {
    return <ImageBody shape={shape} ghost />;
  }
  /** Exports embed the original image, cropped and flipped as on the canvas. */
  async toSvg(shape: TLImageShape) {
    const href = await exportImageData(this.editor, shape);
    if (!href) return null;
    const { w, h, crop, flipX, flipY } = shape.props;
    const left = crop?.topLeft.x ?? 0,
      top = crop?.topLeft.y ?? 0,
      right = crop?.bottomRight.x ?? 1,
      bottom = crop?.bottomRight.y ?? 1;
    const fullW = w / Math.max(0.0001, right - left),
      fullH = h / Math.max(0.0001, bottom - top);
    const id = `image-crop-${shape.id.replaceAll(":", "-")}`;
    const flip = `translate(${flipX ? w : 0} ${flipY ? h : 0}) scale(${flipX ? -1 : 1} ${flipY ? -1 : 1})`;
    return (
      <g>
        <defs>
          <clipPath id={id}>
            <rect width={w} height={h} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${id})`}>
          <g transform={flip}>
            <image
              href={href}
              x={-(flipX ? 1 - right : left) * fullW}
              y={-(flipY ? 1 - bottom : top) * fullH}
              width={fullW}
              height={fullH}
              preserveAspectRatio={shape.meta.imageFit==="contain"?"xMidYMid meet":"none"}
            />
          </g>
        </g>
      </g>
    );
  }
}

/** The original image as a data URL; a standalone SVG cannot fetch it later. */
async function exportImageData(editor: PlanroomEditor, shape: TLImageShape) {
  const asset = shape.props.assetId
    ? editor.getAsset<TLImageAsset>(shape.props.assetId)
    : undefined;
  const store = (editor.store.props as { assets?: TLAssetStore }).assets;
  const url = asset
    ? await (store?.resolve
        ? store.resolve(asset, {
            screenScale: 1,
            steppedScreenScale: 1,
            dpr: 1,
            networkEffectiveType: null,
            shouldResolveToOriginal: true,
          })
        : asset.props.src)
    : shape.props.url;
  if (!url) return null;
  if (url.startsWith("data:")) return url;
  const response = await fetch(url);
  if (!response.ok) throw new Error("An image could not be loaded for export.");
  return FileHelpers.blobToDataUrl(await response.blob());
}

// The last URL drawn for each asset record, so a remounted image starts from it
// rather than blank. Object URLs are left out: they are revoked once uploaded.
const lastDrawn = new WeakMap<TLImageAsset, string>();

/** Resolve the asset URL for the size the image is drawn at on screen. */
function useImageSource(editor: PlanroomEditor, shape: TLImageShape) {
  const asset = useValue(
    "image asset",
    () =>
      shape.props.assetId
        ? editor.getAsset<TLImageAsset>(shape.props.assetId)
        : undefined,
    [editor, shape.props.assetId],
  );
  // Power-of-two steps keep zooming from re-resolving on every frame.
  const stepped = useValue(
    "image scale",
    () => {
      const native = asset?.props.w || shape.props.w;
      const scale = (shape.props.w * editor.getZoomLevel()) / native;
      return 2 ** Math.ceil(Math.log2(Math.max(scale, 1 / 64)));
    },
    [editor, asset?.props.w, shape.props.w],
  );
  // Asset stores may load a sharper source only for images actually on screen.
  const onScreen = useValue(
    "image on screen",
    () =>
      editor
        .getShapePageBounds(shape.id)
        ?.collides(editor.getViewportPageBounds()) ?? false,
    [editor, shape.id],
  );
  const [src, setSrc] = useState<string | null>(
    () => (asset && lastDrawn.get(asset)) ?? null,
  );
  const drawn = useRef(src);
  useEffect(() => {
    if (!asset) {
      drawn.current = shape.props.url || null;
      setSrc(drawn.current);
      return;
    }
    const store = (editor.store.props as { assets?: TLAssetStore }).assets;
    let cancelled = false;
    const context = {
      screenScale: stepped,
      steppedScreenScale: stepped,
      dpr: window.devicePixelRatio || 1,
      networkEffectiveType: null,
      shouldResolveToOriginal: false,
      onScreen,
    };
    const resolved = store?.resolve
      ? store.resolve(asset, context)
      : asset.props.src;
    Promise.resolve(resolved).then((url) => {
      if (cancelled) return;
      const next = (url as string | null) ?? null;
      const show = () => {
        if (cancelled) return;
        if (next && !next.startsWith("blob:")) lastDrawn.set(asset, next);
        drawn.current = next;
        setSrc(next);
      };
      if (next === drawn.current) return;
      if (!next || !drawn.current) return show();
      // Keep the current image until the next size has decoded, so zooming
      // swaps resolution without a blank frame.
      const preload = new Image();
      preload.src = next;
      preload.decode().then(show, show);
    });
    return () => {
      cancelled = true;
    };
  }, [editor, asset, stepped, onScreen, shape.props.url]);
  return src;
}

/** The image, or (as `ghost`) the whole image faintly, for crop mode. */
function ImageBody({ shape, ghost = false }: { shape: TLImageShape; ghost?: boolean }) {
  const editor = useEditor();
  const src = useImageSource(editor, shape);
  const { w, h, crop, flipX, flipY, altText } = shape.props;
  const left = crop?.topLeft.x ?? 0,
    top = crop?.topLeft.y ?? 0,
    right = crop?.bottomRight.x ?? 1,
    bottom = crop?.bottomRight.y ?? 1;
  // The full image is scaled so the cropped part fills the shape.
  const fullW = w / Math.max(0.0001, right - left),
    fullH = h / Math.max(0.0001, bottom - top);
  const image = (extra?: { opacity: number }) => (
    <img
      src={src!}
      alt={extra ? "" : altText}
      draggable={false}
      style={{
        position: "absolute",
        width: fullW,
        height: fullH,
        left: -left * fullW,
        top: -top * fullH,
        maxWidth: "none",
        objectFit:shape.meta.imageFit==="contain"?"contain":"fill",
        transform: `scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})`,
        transformOrigin: `${(left + right) * 50}% ${(top + bottom) * 50}%`,
        userSelect: "none",
        ...extra,
      }}
    />
  );
  if (ghost)
    return src ? (
      <HTMLContainer style={{ width: w, height: h, overflow: "visible", pointerEvents: "none" }}>
        {image({ opacity: 0.35 })}
      </HTMLContainer>
    ) : null;
  return (
    <HTMLContainer style={{ width: w, height: h, overflow: "hidden" }}>
      {src ? image() : <div className="canvas-image-placeholder" />}
      {shape.meta.variantReviewEnabled === true && (
        <label
          style={{ position: "absolute", left: 8, bottom: 8, padding: "4px 8px",
            background: "white", color: "#25343b", fontSize: 12,
            pointerEvents: "all", border: "1px solid #d6dad3" }}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <input
            type="checkbox"
            aria-label={`Pick ${altText || "variant"}`}
            checked={shape.meta.variantSelected === true}
            disabled={editor.getIsReadonly() || editor.isShapeOrAncestorLocked(shape)}
            onChange={(event) => {
              editor.markHistoryStoppingPoint("Pick variant");
              editor.updateShape({
                id: shape.id, type: "image",
                meta: { ...shape.meta, variantSelected: event.target.checked,
                  variantStatus: event.target.checked ? "selected" : "undecided" },
              });
            }}
          /> Pick
        </label>
      )}
    </HTMLContainer>
  );
}
